/**
 * TelegramShell — Shared wrapper for all Telegram Mini App pages.
 *
 * Handles:
 * 1. Telegram WebApp SDK loading + initialization
 * 2. Authentication via initData → JWT stored in localStorage
 * 3. AuthProvider + SessionProvider wrapping (so components can use useAuth/useSession)
 * 4. Telegram theme integration
 * 5. Back button → close WebApp
 * 6. Loading and error states
 */

import React, { useState, useEffect } from 'react';
import { AuthProvider } from '../contexts/AuthContext';
import { SubscriptionProvider } from '../contexts/SubscriptionContext';
import { SessionProvider } from '../contexts/SessionContext';

// Telegram WebApp type declarations
declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        initData: string;
        initDataUnsafe: { user?: { id: number; first_name: string } };
        ready: () => void;
        expand: () => void;
        close: () => void;
        themeParams: Record<string, string | undefined>;
        BackButton: { show: () => void; hide: () => void; onClick: (cb: () => void) => void };
        isExpanded: boolean;
      };
    };
  }
}

interface TelegramShellProps {
  children: React.ReactNode;
  /** Show back button that closes the Mini App (default: true) */
  showBackButton?: boolean;
}

const TelegramShell: React.FC<TelegramShellProps> = ({ children, showBackButton = true }) => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      // Load Telegram SDK if not already present
      if (!window.Telegram?.WebApp) {
        if (!document.querySelector('script[src*="telegram-web-app"]')) {
          const script = document.createElement('script');
          script.src = 'https://telegram.org/js/telegram-web-app.js';
          document.head.appendChild(script);
        }
        // Wait for SDK (up to 2 seconds)
        await new Promise<void>((resolve) => {
          let attempts = 0;
          const check = setInterval(() => {
            if (window.Telegram?.WebApp || ++attempts > 20) {
              clearInterval(check);
              resolve();
            }
          }, 100);
        });
      }

      const tg = window.Telegram?.WebApp;
      if (tg) {
        tg.ready();
        tg.expand();
        if (showBackButton) {
          try {
            tg.BackButton.show();
            tg.BackButton.onClick(() => tg.close());
          } catch {}
        }
      }

      // Authenticate
      const initData = tg?.initData;
      if (initData) {
        try {
          const resp = await fetch('/api/auth/telegram/webapp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ init_data: initData }),
          });
          const data = await resp.json();
          if (data.token) {
            localStorage.setItem('access_token', data.token);
            if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
          } else {
            setError('Authentication failed.');
            setReady(true);
            return;
          }
        } catch {
          setError('Could not connect. Please try again.');
          setReady(true);
          return;
        }
      } else if (!localStorage.getItem('access_token')) {
        setError('Please open this from the Lomi Telegram bot.');
        setReady(true);
        return;
      }

      setReady(true);
    };
    init();
  }, [showBackButton]);

  // Apply Telegram theme colors
  useEffect(() => {
    const tp = window.Telegram?.WebApp?.themeParams;
    if (tp) {
      const root = document.documentElement;
      if (tp.bg_color) root.style.setProperty('--tg-bg', tp.bg_color);
      if (tp.text_color) root.style.setProperty('--tg-text', tp.text_color);
      if (tp.button_color) root.style.setProperty('--tg-button', tp.button_color);
      if (tp.secondary_bg_color) root.style.setProperty('--tg-bg2', tp.secondary_bg_color);
    }
  }, []);

  if (!ready) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 16 }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(0,0,0,0.1)', borderTopColor: '#4a7c10', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ fontSize: 15, opacity: 0.6 }}>Loading...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 16, padding: 20, textAlign: 'center' }}>
        <p>⚠️ {error}</p>
        <button
          onClick={() => window.Telegram?.WebApp?.close()}
          style={{ padding: '10px 24px', border: 'none', borderRadius: 8, background: '#4a7c10', color: 'white', fontSize: 15, cursor: 'pointer' }}
        >
          Close
        </button>
      </div>
    );
  }

  // AuthProvider will auto-detect the JWT in localStorage
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <SessionProvider>
          {children}
        </SessionProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
};

export default TelegramShell;
